import type { ProductFieldMappingRow, SkuProductFieldOption } from './product-field-mapping.types';

export const FIELD_MAPPING_CONFIG_KEYS = [
  'productFieldMappingConfig',
  'fieldMappingConfig',
  'magentoFieldMappingConfig',
] as const;
export const FIELD_MAPPING_ARRAY_KEYS = ['productFieldMappings', 'fieldMappings'] as const;

/** Parse from API: JSON config string or legacy array. Required rows first, then optional with saved value. */
export function parseFieldMappingFromApi(
  apiItem: Record<string, unknown> | null | undefined,
  fieldsConfig: SkuProductFieldOption[],
  options?: {
    configKeys?: readonly string[];
    arrayKeys?: readonly string[];
    skuCodeKey?: string;
    attributeKey?: string;
  }
): ProductFieldMappingRow[] {
  const configKeys = options?.configKeys ?? FIELD_MAPPING_CONFIG_KEYS;
  const arrayKeys = options?.arrayKeys ?? FIELD_MAPPING_ARRAY_KEYS;
  const skuCodeKey = options?.skuCodeKey ?? 'skuFieldCode';
  const attrKey = options?.attributeKey ?? 'magentoAttribute';

  const aliasSku = (m: Record<string, unknown>): string =>
    (m[skuCodeKey] ?? m['tmaFieldCode'] ?? m['tmaField']) as string ?? '';
  const aliasAttr = (m: Record<string, unknown>): string =>
    (m[attrKey] ?? m['magentoAttributeCode'] ?? m['magentoField']) as string ?? '';

  let savedBySku: Record<string, string> = {};

  const jsonConfig = configKeys
    .map((k) => apiItem?.[k])
    .find((v) => typeof v === 'string' && (v as string).trim());
  if (jsonConfig && typeof jsonConfig === 'string') {
    try {
      const parsed: unknown = JSON.parse(jsonConfig);
      if (Array.isArray(parsed)) {
        (parsed as Record<string, unknown>[]).forEach((m) => {
          const code = aliasSku(m);
          const attr = aliasAttr(m);
          if (code) savedBySku[code] = attr;
        });
      } else if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        savedBySku = { ...(parsed as Record<string, string>) };
      }
    } catch {
      savedBySku = {};
    }
  } else {
    const raw = arrayKeys
      .map((k) => apiItem?.[k])
      .find((v): v is unknown[] => Array.isArray(v));
    if (raw) {
      (raw as Record<string, unknown>[]).forEach((m) => {
        const code = aliasSku(m);
        const attr = aliasAttr(m);
        if (code) savedBySku[code] = attr;
      });
    }
  }

  const required = fieldsConfig.filter((f) => f.required);
  const optional = fieldsConfig.filter((f) => !f.required);
  const requiredRows: ProductFieldMappingRow[] = required.map((f) => ({
    skuFieldCode: f.code,
    magentoAttribute: savedBySku[f.code] ?? '',
  }));
  const optionalRows: ProductFieldMappingRow[] = optional
    .filter((f) => savedBySku[f.code] !== undefined)
    .map((f) => ({
      skuFieldCode: f.code,
      magentoAttribute: savedBySku[f.code] ?? '',
    }));
  return [...requiredRows, ...optionalRows];
}

export function serializeFieldMappingToConfig(
  mappings: ProductFieldMappingRow[],
  options?: { skuCodeKey?: string; attributeKey?: string; directionKey?: string }
): string {
  const skuCodeKey = options?.skuCodeKey ?? 'skuFieldCode';
  const attributeKey = options?.attributeKey ?? 'magentoAttribute';
  const directionKey = options?.directionKey ?? 'syncDirection';
  const list = mappings.map((m) => ({
    [skuCodeKey]: m.skuFieldCode,
    [attributeKey]: m.magentoAttribute ?? '',
    [directionKey]: m.syncDirection ?? 'BIDIRECTIONAL',
  }));
  return JSON.stringify(list);
}

export function getOptionalFieldsForDropdown(
  mappings: ProductFieldMappingRow[],
  fieldsConfig: SkuProductFieldOption[]
): { label: string; value: string }[] {
  const existing = new Set(mappings.map((m) => m.skuFieldCode));
  return fieldsConfig
    // Show both required and optional fields that are not yet in the table
    .filter((f) => !existing.has(f.code))
    .map((f) => ({ label: f.label ?? f.labelKey ?? f.code, value: f.code }));
}

/** Build table rows with id, isRequired, and placeholder. */
export function getProductFieldMappingTableData(
  mappings: ProductFieldMappingRow[],
  fieldsConfig: SkuProductFieldOption[],
  requiredFieldCodes: string[],
  translateFn: (key: string, params?: Record<string, string>) => string,
  placeholderKey: string
): Record<string, unknown>[] {
  const byCode = new Map(fieldsConfig.map((f) => [f.code, f]));
  return mappings.map((m) => {
    const field = byCode.get(m.skuFieldCode);
    const keyLabel = field ? (field.label ?? (field.labelKey ? translateFn(field.labelKey) : field.code)) : m.skuFieldCode || '—';
    return {
      ...m,
      syncDirection: m.syncDirection ?? 'BIDIRECTIONAL',
      dataType: m.dataType ?? 'STRING',
      id: m.skuFieldCode,
      /** Display label for Trường SKU column (label instead of code) */
      skuFieldCodeLabel: keyLabel,
      isRequired: requiredFieldCodes.includes(m.skuFieldCode),
      magentoAttributePlaceholder: translateFn(placeholderKey, { keyLabel }),
    };
  });
}
